import type { ChecklistItem } from '../../../types/schema'
import type {
  AiRetrievalRecord,
  AiRetrievalScopeProvider,
  AiRetrievalScopeProviderInput,
} from './retrievalTypes'

const asIso = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string') return Number.isFinite(new Date(value).getTime()) ? value : null
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
    const date = value.toDate()
    return date instanceof Date ? date.toISOString() : null
  }
  return null
}

const checklistBlocked = (item: ChecklistItem) =>
  Boolean(
    item.blockedByExternal
    || item.blockedByExternalId
    || (item.prerequisites ?? []).length
    || (item.dependencyNodeIds ?? []).length
    || (item.blockedByNodeIds ?? []).length
  )

const checklistRiskSignals = (item: ChecklistItem) => [
  checklistBlocked(item) ? 'dependency blocker' : '',
  item.requiresProofFile ? 'proof required' : '',
  item.requiresLocationPin ? 'location required' : '',
  item.requiresCoSign ? 'co-sign required' : '',
  item.isReady === false ? 'resource not ready' : '',
].filter(Boolean)

export const timelineScopeProvider: AiRetrievalScopeProvider = {
  scope: 'timeline',
  retrieve: (input) => (input.elements ?? [])
    .filter((element) => element.workspaceId === input.workspaceId)
    .map<AiRetrievalRecord>((element) => ({
      id: element.id,
      workspaceId: input.workspaceId,
      scope: 'timeline',
      layer: input.selectedTargets?.some((target) => target.id === element.id) ? 'immediate_request' : 'active_operational',
      type: element.type,
      title: element.title,
      summary: element.description?.trim() || `${element.type.toLowerCase()} on ${element.board ?? 'DASHBOARD'}`,
      targetRef: {
        id: element.id,
        type: element.type,
        title: element.title,
        board: element.board,
        parentId: element.parentId,
      },
      parentId: element.parentId,
      board: element.board ?? 'DASHBOARD',
      status: element.boardStatus ?? element.workflowStatus ?? null,
      workflowStatus: element.workflowStatus ?? null,
      progress: element.progress,
      dueDateIso: element.endDate ?? null,
      updatedAtIso: asIso(element.updatedAt),
      dependencyIds: element.dependencyNodeIds ?? [],
      assigneeIds: element.assignedEditors ?? [],
      urgency: element.boardStatus === 'IN_PROGRESS' || element.workflowStatus === 'IN_PROGRESS' ? 8 : 0,
      isBlocked: Boolean((element.dependencyNodeIds ?? []).length),
      riskSignals: (element.dependencyNodeIds ?? []).length ? ['dependency participant'] : [],
      source: 'LOCAL_STATE',
    })),
}

export const checklistGraphScopeProvider: AiRetrievalScopeProvider = {
  scope: 'checklist_graph',
  retrieve: (input) => (input.checklistItems ?? [])
    .filter((item) => item.workspaceId === input.workspaceId)
    .map<AiRetrievalRecord>((item) => {
      const riskSignals = checklistRiskSignals(item)
      const dependencyIds = [
        ...(item.prerequisites ?? []),
        ...(item.dependencyNodeIds ?? []),
        ...(item.blockedByNodeIds ?? []).map((link) => link.targetId),
        ...(item.isBlockerForNodeIds ?? []).map((link) => link.targetId),
      ].filter(Boolean)
      return {
        id: item.id,
        workspaceId: input.workspaceId,
        scope: 'checklist_graph',
        layer: dependencyIds.length || riskSignals.length ? 'dependency' : 'active_operational',
        type: 'CHECKLIST',
        title: item.title,
        summary: item.description?.trim() || item.completionCriteria?.trim() || 'Checklist execution node.',
        targetRef: {
          id: item.id,
          type: 'CHECKLIST',
          title: item.title,
          board: item.board,
          parentId: item.parentContainerId,
        },
        parentId: item.parentContainerId,
        board: item.board ?? 'DASHBOARD',
        status: item.boardStatus ?? null,
        workflowStatus: item.workflowStatus ?? null,
        dueDateIso: item.scheduleEndDate ?? item.scheduleStartDate ?? null,
        updatedAtIso: asIso(item.updatedAt),
        dependencyIds,
        blockerIds: (item.blockedByNodeIds ?? []).map((link) => link.targetId),
        assigneeIds: [item.assigneeId].filter((id): id is string => Boolean(id)),
        urgency: Number(item.urgencyStars ?? 0) + Number(item.importanceStars ?? 0) + Number(item.complexityStars ?? 0),
        isBlocked: checklistBlocked(item),
        riskSignals,
        source: 'LOCAL_STATE',
      }
    }),
}

export const workspacePulseScopeProvider: AiRetrievalScopeProvider = {
  scope: 'workspace_pulse',
  retrieve: (input) => (input.pulseHints ?? []).map<AiRetrievalRecord>((hint) => ({
    id: hint.id,
    workspaceId: input.workspaceId,
    scope: 'workspace_pulse',
    layer: hint.severity === 'BLOCKER' || hint.severity === 'CRITICAL' ? 'execution_risk' : 'governance',
    type: 'PULSE',
    title: hint.summary.slice(0, 80),
    summary: hint.summary,
    targetRef: hint.targetId ? { id: hint.targetId, type: 'WORKSPACE', title: hint.summary.slice(0, 80) } : undefined,
    linkedRecordIds: hint.targetId ? [hint.targetId] : [],
    riskSignals: hint.severity ? [hint.severity] : [],
    urgency: hint.severity === 'BLOCKER' || hint.severity === 'CRITICAL' ? 20 : hint.severity === 'WARN' || hint.severity === 'WATCH' ? 10 : 4,
    isBlocked: hint.severity === 'BLOCKER',
    source: 'PULSE',
  })),
}

export const cognitiveMemoryScopeProvider: AiRetrievalScopeProvider = {
  scope: 'cognitive_memory',
  retrieve: (input) => (input.memoryInsights ?? []).map<AiRetrievalRecord>((insight) => ({
    id: insight.id,
    workspaceId: input.workspaceId,
    scope: 'cognitive_memory',
    layer: 'strategic_memory',
    type: 'MEMORY',
    title: insight.kind.replace(/_/g, ' ').toLowerCase(),
    summary: insight.summary,
    linkedRecordIds: insight.evidenceRecordIds,
    urgency: Math.round(insight.confidence * 10),
    source: 'MEMORY',
    metadata: {
      confidence: insight.confidence,
      retentionDays: insight.retentionDays,
      memorySource: insight.source,
    },
  })),
}

export const supplementalScopeProvider: AiRetrievalScopeProvider = {
  scope: 'governance_activity',
  retrieve: (input: AiRetrievalScopeProviderInput) =>
    (input.supplementalRecords ?? []).filter((record) => record.workspaceId === input.workspaceId),
}

export const DEFAULT_RETRIEVAL_SCOPE_PROVIDERS: AiRetrievalScopeProvider[] = [
  timelineScopeProvider,
  checklistGraphScopeProvider,
  workspacePulseScopeProvider,
  cognitiveMemoryScopeProvider,
  supplementalScopeProvider,
]
